<template>
  <BaseReport
    ref="baseReport"
    :loading="loading"
    :reportTitle="reportTitle"
    :reportSubtitle="dateRangeLabel"
    :columns="6"
    @mounted="onMounted"
  >
    <template #controls>
      <div class="report-controls">
        <AnalyticsDateRangePicker
          :modelValue="{ startDate: rangeSelection.startDate, endDate: rangeSelection.endDate }"
          @update:modelValue="handleDateRangeChange"
        />
        <AnalyticsPeriodPicker
          v-model="rangeSelection.intervalType"
          @update:modelValue="handlePeriodChange"
        />
      </div>
    </template>

    <template #header>
      <h2>Counselors</h2>
      <p>Compare counselor performance for tickets created in the selected date range. Click any chart bar or table row to drill down.</p>
    </template>

    <template #content>
      <!-- Empty state when no data -->
      <div v-if="tableRows.length === 0" class="empty-state">
        <p>Select a date range to view counselor performance metrics</p>
      </div>

      <!-- Report content when data available -->
      <template v-else>
        <!-- Tickets Chart -->
        <CardBarChart
          class="cell cell-chart"
          title="Tickets"
          subtitle="Per counselor, new vs existing"
          :data="ticketsChartData"
          :options="ticketsChartOptions"
          variant="stacked"
          :horizontal="true"
          :height="chartHeight"
          @bar-click="handleBarChartClick"
        />

        <!-- Sales Chart -->
        <CardBarChart
          class="cell cell-chart"
          title="Sales"
          subtitle="Per counselor, new vs existing"
          :data="salesChartData"
          :options="salesChartOptions"
          variant="stacked"
          :horizontal="true"
          :height="chartHeight"
          @bar-click="handleBarChartClick"
        />

        <!-- Average Price Chart -->
        <CardBarChart
          class="cell cell-chart"
          title="Average Price"
          subtitle="Per counselor, new vs existing"
          :data="avgPriceChartData"
          :options="avgPriceChartOptions"
          variant="grouped"
          :horizontal="true"
          :height="chartHeight"
          @bar-click="handleBarChartClick"
        />

        <!-- Sales Trend Line Chart -->
        <CardLineChart
          class="cell cell-trend-chart"
          title="Sales Over Time"
          :subtitle="trendSubtitle"
          variant="timeseries"
          :data="trendChartData"
          :options="trendChartOptions"
          :formatTooltipValue="formatCurrencyValue"
          :height="350"
          @point-click="handleTrendChartClick"
        />

        <!-- Detailed Metrics Table -->
        <CardTable
          class="cell cell-metrics-table"
          title="Detailed Performance Breakdown"
          subtitle="Click row to drill down"
          :columns="tableColumns"
          :rows="filteredTableRows"
          variant="summary"
          :summaryRow="summaryRow"
          :maxRows="100"
          :clickable="true"
          :stickyFirstColumn="true"
          @row-click="handleCounselorClick"
        />
      </template>
    </template>
  </BaseReport>
</template>

<script>
import BaseReport from '@/components/common/BaseReport.vue';
import CardTable from '@/components/common/CardTable.vue';
import CardBarChart from '@/components/common/CardBarChart.vue';
import CardLineChart from '@/components/common/CardLineChart.vue';
import AnalyticsDateRangePicker from '@/components/inputs/AnalyticsDateRangePicker.vue';
import AnalyticsPeriodPicker from '@/components/inputs/AnalyticsPeriodPicker.vue';
import * as api from '@/api';
import { alertService } from '@/libs/alert';
import { serializeFilters } from '@/utils/filterSerializer';
import { formatDateShort } from '@/utils/formatters';

export default {
  name: 'CounselorsReport',
  components: {
    BaseReport,
    CardTable,
    CardBarChart,
    CardLineChart,
    AnalyticsDateRangePicker,
    AnalyticsPeriodPicker
  },
  emits: ['loading'],
  data() {
    return {
      reportTitle: 'Counselor Performance Comparison',
      loading: true,

      // Controls
      rangeSelection: {
        startDate: null,
        endDate: null,
        intervalType: 'weekly'
      },
      selectedCounselorIds: [],

      // Data
      comparisonData: null,
      trendData: null,
      periodDates: [],

      // Table data
      tableRows: [],
      tableColumns: [
        { key: 'counselor', label: 'Counselor' },
        { key: 'npTickets', label: 'NP Tickets', format: 'number' },
        { key: 'currTickets', label: 'EP Tickets', format: 'number' },
        { key: 'allTickets', label: 'Total Tickets', format: 'number' },
        { key: 'npAvg', label: 'NP Avg', format: 'currency' },
        { key: 'currAvg', label: 'EP Avg', format: 'currency' },
        { key: 'allAvg', label: 'Overall Avg', format: 'currency' },
        { key: 'npSales', label: 'NP Sales', format: 'currency' },
        { key: 'currSales', label: 'EP Sales', format: 'currency' },
        { key: 'allSales', label: 'Total Sales', format: 'currency' }
      ]
    };
  },
  computed: {
    dateRangeLabel() {
      if (!this.rangeSelection.startDate || !this.rangeSelection.endDate) {
        return '';
      }
      return `${formatDateShort(this.rangeSelection.startDate)} - ${formatDateShort(this.rangeSelection.endDate)}`;
    },
    counselorFilterOptions() {
      return this.tableRows.map(row => ({
        value: row.counselorId,
        label: row.counselor
      }));
    },
    filteredTableRows() {
      if (this.selectedCounselorIds.length === 0) {
        return this.tableRows;
      }
      return this.tableRows.filter(row =>
        this.selectedCounselorIds.includes(row.counselorId)
      );
    },
    summaryRow() {
      if (this.filteredTableRows.length === 0) return null;

      const totals = this.filteredTableRows.reduce(
        (acc, row) => {
          acc.npTickets += row.npTickets || 0;
          acc.npSales += row.npSales || 0;
          acc.currTickets += row.currTickets || 0;
          acc.currSales += row.currSales || 0;
          acc.allTickets += row.allTickets || 0;
          acc.allSales += row.allSales || 0;
          return acc;
        },
        {
          npTickets: 0,
          npSales: 0,
          currTickets: 0,
          currSales: 0,
          allTickets: 0,
          allSales: 0
        }
      );

      const npAvg = totals.npTickets > 0 ? totals.npSales / totals.npTickets : 0;
      const currAvg = totals.currTickets > 0 ? totals.currSales / totals.currTickets : 0;
      const allAvg = totals.allTickets > 0 ? totals.allSales / totals.allTickets : 0;

      return {
        counselor: 'Total',
        npTickets: totals.npTickets,
        npSales: totals.npSales,
        npAvg,
        currTickets: totals.currTickets,
        currSales: totals.currSales,
        currAvg,
        allTickets: totals.allTickets,
        allSales: totals.allSales,
        allAvg
      };
    },
    sortedTableRows() {
      return [...this.filteredTableRows].sort((a, b) => {
        const totalA = (a.npTickets || 0) + (a.currTickets || 0);
        const totalB = (b.npTickets || 0) + (b.currTickets || 0);
        return totalA - totalB;
      });
    },
    ticketsChartData() {
      if (this.filteredTableRows.length === 0) return null;

      const sorted = [...this.filteredTableRows].sort((a, b) => {
        const totalA = (a.npTickets || 0) + (a.currTickets || 0);
        const totalB = (b.npTickets || 0) + (b.currTickets || 0);
        return totalB - totalA;
      });

      const top = sorted.slice(0, 7);
      const rest = sorted.slice(7);
      const chartData = [...top];

      if (rest.length > 0) {
        chartData.push({
          counselor: 'Other',
          currTickets: rest.reduce((sum, r) => sum + (r.currTickets || 0), 0),
          npTickets: rest.reduce((sum, r) => sum + (r.npTickets || 0), 0)
        });
      }

      const reversed = chartData.reverse();
      return {
        labels: reversed.map(row => row.counselor),
        datasets: [
          {
            label: 'Existing Patient',
            data: reversed.map(row => row.currTickets || 0),
            color: '#4338ca'
          },
          {
            label: 'New Patient',
            data: reversed.map(row => row.npTickets || 0),
            color: '#818cf8'
          }
        ]
      };
    },
    salesChartData() {
      if (this.filteredTableRows.length === 0) return null;

      const sorted = [...this.filteredTableRows].sort((a, b) => {
        const totalA = (a.npSales || 0) + (a.currSales || 0);
        const totalB = (b.npSales || 0) + (b.currSales || 0);
        return totalB - totalA;
      });

      const top = sorted.slice(0, 7);
      const rest = sorted.slice(7);
      const chartData = [...top];

      if (rest.length > 0) {
        chartData.push({
          counselor: 'Other',
          currSales: rest.reduce((sum, r) => sum + (r.currSales || 0), 0),
          npSales: rest.reduce((sum, r) => sum + (r.npSales || 0), 0)
        });
      }

      const reversed = chartData.reverse();
      return {
        labels: reversed.map(row => row.counselor),
        datasets: [
          {
            label: 'Existing Patient',
            data: reversed.map(row => row.currSales || 0),
            color: '#4338ca'
          },
          {
            label: 'New Patient',
            data: reversed.map(row => row.npSales || 0),
            color: '#818cf8'
          }
        ]
      };
    },
    avgPriceChartData() {
      if (this.filteredTableRows.length === 0) return null;

      const sorted = [...this.filteredTableRows].sort((a, b) => {
        const totalA = (a.npAvg || 0) + (a.currAvg || 0);
        const totalB = (b.npAvg || 0) + (b.currAvg || 0);
        return totalB - totalA;
      });

      // For avg price, take top 7 but don't aggregate "Other" since averaging averages is misleading
      const top = sorted.slice(0, 7);
      const reversed = top.reverse();
      return {
        labels: reversed.map(row => row.counselor),
        datasets: [
          {
            label: 'Existing Patient',
            data: reversed.map(row => row.currAvg || 0),
            color: '#4338ca'
          },
          {
            label: 'New Patient',
            data: reversed.map(row => row.npAvg || 0),
            color: '#818cf8'
          }
        ]
      };
    },
    ticketsChartOptions() {
      return {
        tooltip: {
          formatter: (params) => {
            if (!Array.isArray(params)) params = [params];

            let total = 0;
            let html = `<div style="font-weight: 600; margin-bottom: 8px;">${params[0].axisValue}</div>`;

            params.forEach(param => {
              const value = param.value || 0;
              total += value;
              const color = param.color;
              html += `<div style="display: flex; align-items: center; margin-bottom: 4px;">`;
              html += `<span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: ${color}; margin-right: 8px;"></span>`;
              html += `<span style="flex: 1;">${param.seriesName}:</span>`;
              html += `<span style="font-weight: 600; margin-left: 12px;">${value.toLocaleString()} tickets</span>`;
              html += `</div>`;
            });

            html += `<div style="border-top: 1px solid rgba(255,255,255,0.2); margin-top: 8px; padding-top: 8px; font-weight: 600;">`;
            html += `<span>Total:</span>`;
            html += `<span style="float: right; margin-left: 12px;">${total.toLocaleString()} tickets</span>`;
            html += `</div>`;

            return html;
          }
        }
      };
    },
    salesChartOptions() {
      return {
        tooltip: {
          formatter: (params) => {
            if (!Array.isArray(params)) params = [params];

            let total = 0;
            let html = `<div style="font-weight: 600; margin-bottom: 8px;">${params[0].axisValue}</div>`;

            params.forEach(param => {
              const value = param.value || 0;
              total += value;
              const color = param.color;
              const formatted = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
              html += `<div style="display: flex; align-items: center; margin-bottom: 4px;">`;
              html += `<span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: ${color}; margin-right: 8px;"></span>`;
              html += `<span style="flex: 1;">${param.seriesName}:</span>`;
              html += `<span style="font-weight: 600; margin-left: 12px;">${formatted}</span>`;
              html += `</div>`;
            });

            const totalFormatted = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(total);
            html += `<div style="border-top: 1px solid rgba(255,255,255,0.2); margin-top: 8px; padding-top: 8px; font-weight: 600;">`;
            html += `<span>Total:</span>`;
            html += `<span style="float: right; margin-left: 12px;">${totalFormatted}</span>`;
            html += `</div>`;

            return html;
          }
        }
      };
    },
    avgPriceChartOptions() {
      return {
        tooltip: {
          formatter: (params) => {
            if (!Array.isArray(params)) params = [params];

            let html = `<div style="font-weight: 600; margin-bottom: 8px;">${params[0].axisValue}</div>`;

            params.forEach(param => {
              const value = param.value || 0;
              const color = param.color;
              const formatted = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
              html += `<div style="display: flex; align-items: center; margin-bottom: 4px;">`;
              html += `<span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: ${color}; margin-right: 8px;"></span>`;
              html += `<span style="flex: 1;">${param.seriesName}:</span>`;
              html += `<span style="font-weight: 600; margin-left: 12px;">${formatted}</span>`;
              html += `</div>`;
            });

            return html;
          }
        }
      };
    },
    trendChartOptions() {
      if (!this.trendChartData || !this.trendChartData.datasets || this.trendChartData.datasets.length === 0) {
        return null;
      }

      // Get colors from the datasets
      const colors = this.trendChartData.datasets.map(ds => ds.color);

      return {
        yAxis: {
          axisLabel: {
            formatter: (value) => {
              if (value >= 1000000) {
                return '$' + (value / 1000000).toFixed(1) + 'M';
              } else if (value >= 1000) {
                return '$' + (value / 1000).toFixed(0) + 'K';
              }
              return '$' + value.toFixed(2);
            }
          }
        },
        series: this.trendChartData.datasets.map((dataset, index) => ({
          name: dataset.label,
          type: 'line',
          stack: 'total',
          data: dataset.data,
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: {
            width: 2,
            color: colors[index]
          },
          itemStyle: {
            color: colors[index],
            borderColor: '#fff',
            borderWidth: 2
          },
          areaStyle: {
            color: colors[index],
            opacity: 0.8
          },
          emphasis: {
            focus: 'series',
            scale: true,
            itemStyle: {
              borderWidth: 3
            }
          },
          triggerLineEvent: true
        }))
      };
    },
    chartHeight() {
      return 350;
    },
    trendChartData() {
      if (!this.trendData || !this.trendData.counselors) return null;

      // Filter counselors based on selection
      let filteredCounselors = this.selectedCounselorIds.length === 0
        ? this.trendData.counselors
        : this.trendData.counselors.filter(c => this.selectedCounselorIds.includes(c.counselorId));

      // Sort counselors by total sales descending
      filteredCounselors = [...filteredCounselors].sort((a, b) => {
        const totalA = a.periods.reduce((sum, p) => sum + p.totalAmountCents, 0);
        const totalB = b.periods.reduce((sum, p) => sum + p.totalAmountCents, 0);
        return totalB - totalA;
      });

      // Take top 7, aggregate rest into "Other"
      const topCounselors = filteredCounselors.slice(0, 7);
      const otherCounselors = filteredCounselors.slice(7);

      let otherDataset = null;
      if (otherCounselors.length > 0) {
        const periodCount = (this.trendData.periods || []).length;
        const otherPerPeriod = Array(periodCount).fill(0);
        otherCounselors.forEach(c => {
          c.periods.forEach((p, i) => { otherPerPeriod[i] += p.totalAmountCents; });
        });
        const otherTotal = otherPerPeriod.reduce((s, v) => s + v, 0);
        if (otherTotal > 0) {
          otherDataset = {
            label: 'Other',
            data: otherPerPeriod.map(v => v / 100),
            color: '#e0e7ff'
          };
        }
      }

      // Reverse so highest is on top in stacked chart
      const reversed = [...topCounselors].reverse();
      const indigoShades = this.generateIndigoShades(reversed.length);

      const datasets = reversed.map((counselor, index) => ({
        label: counselor.name,
        data: counselor.periods.map(p => p.totalAmountCents / 100),
        color: indigoShades[index]
      }));

      if (otherDataset) {
        datasets.unshift(otherDataset);
      }

      return {
        labels: this.trendData.periods || [],
        datasets
      };
    },
    trendSubtitle() {
      const labels = {
        daily: 'Daily trend',
        weekly: 'Weekly trend',
        monthly: 'Monthly trend',
        quarterly: 'Quarterly trend',
        yearly: 'Yearly trend'
      };
      return labels[this.rangeSelection.intervalType] || 'Trend';
    }
  },
  methods: {
    generateIndigoShades(count) {
      // Generate different shades of indigo from light to dark (matches FinancialsReport)
      const indigoShades = [
        '#c7d2fe', // indigo-200
        '#a5b4fc', // indigo-300
        '#818cf8', // indigo-400
        '#6366f1', // indigo-500
        '#4f46e5', // indigo-600
        '#4338ca', // indigo-700
        '#3730a3', // indigo-800
        '#312e81'  // indigo-900
      ];

      // If we need more colors than we have, repeat the pattern
      if (count > indigoShades.length) {
        const result = [];
        for (let i = 0; i < count; i++) {
          result.push(indigoShades[i % indigoShades.length]);
        }
        return result;
      }

      return indigoShades.slice(0, count);
    },

    async onMounted() {
      this.$emit('loading', false);
      this.loading = false;
    },

    handleDateRangeChange(dateRange) {
      this.rangeSelection.startDate = dateRange.startDate;
      this.rangeSelection.endDate = dateRange.endDate;
      if (this.rangeSelection.startDate && this.rangeSelection.endDate) {
        this.loadData();
      }
    },

    handlePeriodChange() {
      if (this.rangeSelection.startDate && this.rangeSelection.endDate) {
        this.loadTrendData();
      }
    },

    async loadData() {
      if (!this.rangeSelection.startDate || !this.rangeSelection.endDate) {
        return;
      }

      this.loading = true;

      try {
        const result = await api.fetchCounselorsComparison({
          startDate: this.rangeSelection.startDate,
          endDate: this.rangeSelection.endDate
        });

        this.comparisonData = result;
        this.transformDataForTable();
        await this.loadTrendData();
      } catch (err) {
        console.error('Failed to load comparison data:', err);
        alertService.error('Failed to load comparison data: ' + err.message);
      } finally {
        this.loading = false;
      }
    },

    transformDataForTable() {
      if (!this.comparisonData || !this.comparisonData.counselors) {
        this.tableRows = [];
        this.selectedCounselorIds = [];
        return;
      }

      this.tableRows = this.comparisonData.counselors.map(c => ({
        counselorId: c.counselorId,
        counselor: c.counselorName,
        npTickets: c.newPatientTickets,
        npSales: c.newPatientRevenueCents / 100,
        npAvg: c.newPatientAvgCents / 100,
        currTickets: c.currentPatientTickets,
        currSales: c.currentPatientRevenueCents / 100,
        currAvg: c.currentPatientAvgCents / 100,
        allTickets: c.totalTickets,
        allSales: c.totalRevenueCents / 100,
        allAvg: c.totalAvgCents / 100
      }));

      // Initialize selected counselors to empty (none selected)
      this.selectedCounselorIds = [];
    },

    async loadTrendData() {
      if (!this.rangeSelection.startDate || !this.rangeSelection.endDate || this.tableRows.length === 0) {
        return;
      }

      const counselorIds = this.tableRows.map(row => row.counselorId);

      try {
        const result = await api.fetchCounselorsSalesTrend({
          startDate: this.rangeSelection.startDate,
          endDate: this.rangeSelection.endDate,
          counselorIds,
          intervalType: this.rangeSelection.intervalType
        });

        this.trendData = result;
        this.periodDates = result.periodDates || [];
      } catch (err) {
        console.error('Failed to load trend data:', err);
        alertService.error('Failed to load trend data: ' + err.message);
      }
    },

    handleCounselorClick(row) {
      if (!row.counselorId) return;

      const filters = [
        {
          field: 'counselorId',
          operator: 'is',
          value: row.counselorId
        },
        {
          field: 'dateTicketStart',
          operator: 'between',
          from: this.rangeSelection.startDate,
          to: this.rangeSelection.endDate
        }
      ];

      const serializedFilters = serializeFilters(filters);
      this.$router.push({
        path: '/tickets',
        query: { filters: serializedFilters }
      });
    },

    handleCounselorFilterChange(newSelectedIds) {
      this.selectedCounselorIds = newSelectedIds;
    },

    handleBarChartClick(event) {
      // Find the counselor by name from the chart
      const counselorName = event.name;
      const counselor = this.tableRows.find(row => row.counselor === counselorName);
      if (!counselor) return;

      this.navigateToCounselorDetails(counselor.counselorId);
    },

    handleTrendChartClick(event) {
      // Find the counselor by series name
      const counselorName = event.seriesName;
      const counselor = this.tableRows.find(row => row.counselor === counselorName);
      if (!counselor) return;

      this.navigateToCounselorDetails(counselor.counselorId);
    },

    navigateToCounselorDetails(counselorId) {
      this.$router.push({
        path: '/dashboard',
        query: {
          report: 'counselor-details',
          startDate: this.rangeSelection.startDate,
          endDate: this.rangeSelection.endDate,
          intervalType: this.rangeSelection.intervalType,
          counselorId: counselorId
        }
      });
    },

    formatCurrencyValue(value) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
    },

    async generatePDF() {
      this.$refs.baseReport.generateReportPDF({
        title: 'Counselor Performance Comparison',
        subtitle: this.dateRangeLabel,
        kpis: [],
        columns: this.tableColumns,
        rows: this.filteredTableRows,
        summaryRow: this.summaryRow
      });
    }
  }
};
</script>

<style scoped>
.report-controls {
  display: flex;
  gap: 12px;
  align-items: center;
}

.counselor-filter {
  width: 300px;
  flex-shrink: 0;
}

.empty-state {
  grid-column: 1 / -1;
  padding: 64px;
  text-align: center;
  color: #6b7280;
  font-size: 1rem;
}

.cell-chart {
  grid-column: span 2;
}

.cell-trend-chart {
  grid-column: span 6;
}

.cell-metrics-table {
  grid-column: span 6;
}

.cell-metrics-table :deep(.data-table th),
.cell-metrics-table :deep(.data-table td) {
  width: 200px;
  min-width: 200px;
  max-width: 200px;
}
</style>
